import { createAsyncThunk, createSelector, createSlice, } from '@reduxjs/toolkit';
import { signInCandidate } from './service/index.js';
import { sendGlobalTrackingThunk } from '../../redux/core/thunks.js';
import { trackFailedRequest } from '../../tracking/index.js';
import { storageKeys } from '../../configs/constants/storageKeys.js';
import { isInTwoStepWrapper, getTwoStepTrackingComponentState, } from '../../tracking/constants.js';
import { saveHasCompletedCVStorage } from '../../helpers/storage/storage.js';
import { trackEmailAttemptSuccessfulRequest } from '../../tracking/featureSpecific/emailAndPassword/index.js';
import { authSuccessThunk } from '../../redux/core/authSuccess/authSuccessSlice.js';
export const initialLoginState = {
    email: {
        value: '',
        error: false,
        errorMessage: '',
    },
    password: {
        value: '',
        error: false,
        errorMessage: '',
    },
    rememberMe: true,
    isLoading: false,
    errorStatus: null,
    emailChangeAlertStatus: false,
};
export const loginRequestThunk = createAsyncThunk('login/loginRequest', async (_, { rejectWithValue, dispatch, getState }) => {
    const state = getState();
    const { loginSource } = state.core.sourceData;
    const { email, password, rememberMe } = state.forms.login;
    const { activeWrapper, activeView } = state.core.wrapperContext;
    const { lastLoginMethod } = state.core.authSuccess;
    const twoStepsModalComponentState = getTwoStepTrackingComponentState(activeView, lastLoginMethod);
    try {
        dispatch(loginSlice.actions.setErrorStatus(null));
        const loginResponse = await signInCandidate({
            email: email.value,
            password: password.value,
            rememberMe,
            loginSource,
        });
        const componentState = `consentStayLoggedIn_${rememberMe ? 'checked' : 'unchecked'}`;
        if (loginResponse.success) {
            dispatch(sendGlobalTrackingThunk(trackEmailAttemptSuccessfulRequest, {
                componentState,
                eventName: ['candidateLogin'],
            }));
            saveHasCompletedCVStorage(loginResponse.hasCompleteCV);
            dispatch(loginSlice.actions.setEmailChangeAlertStatus(false));
            sessionStorage.removeItem(storageKeys.isEmailChangeSuccessful);
            await dispatch(authSuccessThunk('email', 200));
            return;
        }
        if (loginResponse.error) {
            dispatch(sendGlobalTrackingThunk(trackFailedRequest, {
                formFailedAttemptsErrors: 'loginPasswordDoNotMatch',
                ...(isInTwoStepWrapper(activeWrapper) && {
                    componentState: twoStepsModalComponentState,
                }),
            }));
            return rejectWithValue(400);
        }
        return rejectWithValue(loginResponse.statusCode);
    }
    catch (error) {
        dispatch(sendGlobalTrackingThunk(trackFailedRequest, {
            formFailedAttemptsErrors: 'loginPasswordDoNotMatch',
            ...(isInTwoStepWrapper(activeWrapper) && {
                componentState: twoStepsModalComponentState,
            }),
        }));
        return rejectWithValue(500);
    }
});
export const loginSlice = createSlice({
    name: 'login',
    initialState: initialLoginState,
    reducers: {
        updateEmail: (state, actions) => {
            state.email.value = actions.payload;
        },
        updatePassword: (state, actions) => {
            state.password.value = actions.payload;
        },
        toggleRememberMe: (state) => {
            state.rememberMe = !state.rememberMe;
        },
        setIsLoading: (state, actions) => {
            state.isLoading = actions.payload;
        },
        updateEmailError: (state, actions) => {
            state.email.error = actions.payload;
        },
        updateEmailErrorMessage: (state, actions) => {
            state.email.errorMessage = actions.payload;
        },
        updatePasswordError: (state, actions) => {
            state.password.error = actions.payload;
        },
        updatePasswordErrorMessage: (state, actions) => {
            state.password.errorMessage = actions.payload;
        },
        setEmailChangeAlertStatus: (state, actions) => {
            state.emailChangeAlertStatus = actions.payload;
        },
        clearLoginInputs: (state) => {
            state.email.error = false;
            state.email.errorMessage = '';
            state.password.error = false;
            state.password.errorMessage = '';
            state.errorStatus = null;
        },
        setUserLoggedIn: (state, _actions) => {
            state.errorStatus = null;
        },
        setErrorStatus: (state, actions) => {
            state.errorStatus = actions.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginRequestThunk.pending, (state) => {
            state.isLoading = true;
        })
            .addCase(loginRequestThunk.rejected, (state, action) => {
            state.errorStatus = action.payload;
            state.isLoading = false;
        });
    },
});
export const { setIsLoading, toggleRememberMe, updateEmail, updatePassword, updateEmailError, updatePasswordError, setUserLoggedIn, updateEmailErrorMessage, updatePasswordErrorMessage, clearLoginInputs, setErrorStatus, setEmailChangeAlertStatus, } = loginSlice.actions;
const loginState = (state) => state.forms.login;
export const emailSelector = createSelector(loginState, ({ email }) => email);
export const passwordSelector = createSelector(loginState, ({ password }) => password);
export const credentialsSelector = createSelector(loginState, ({ password, email, rememberMe }) => ({ password, email, rememberMe }));
export const rememberMeSelector = createSelector(loginState, ({ rememberMe }) => rememberMe);
export const isLoadingSelector = createSelector(loginState, ({ isLoading }) => isLoading);
export const errorStatusSelector = createSelector(loginState, ({ errorStatus }) => errorStatus);
export const emailChangeAlertStatusSelector = createSelector(loginState, ({ emailChangeAlertStatus }) => emailChangeAlertStatus);
export default loginSlice.reducer;
//# sourceMappingURL=loginSlice.js.map